import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSession } from "./useAuthSession";

export interface PlatformSetting {
  id: string;
  setting_key: string;
  setting_value: any;
  setting_type: 'boolean' | 'string' | 'number' | 'json';
  category: string;
  display_name: string;
  description: string | null;
  is_public: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

// Public branding settings that should load without authentication
const PUBLIC_SETTINGS = [
  'platform_name',
  'platform_tagline',
  'platform_logo_url',
  'platform_mark_logo_url',
  'platform_favicon_url',
  'support_email',
] as const;

export function usePlatformSettings(category?: string) {
  return useQuery({
    queryKey: ['platform-settings', category],
    queryFn: async () => {
      let query = supabase
        .from('platform_settings' as any)
        .select('*')
        .order('display_name');

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as unknown as PlatformSetting[];
    },
  });
}

export function usePlatformSetting(settingKey: string) {
  const { data: session, isLoading: authLoading } = useAuthSession();
  
  // Check if this is a public setting that doesn't require auth
  const isPublicSetting = PUBLIC_SETTINGS.includes(settingKey as typeof PUBLIC_SETTINGS[number]);
  
  const query = useQuery({
    queryKey: ['platform-setting', settingKey, isPublicSetting ? 'public' : session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings' as any)
        .select('*')
        .eq('setting_key', settingKey)
        .maybeSingle();

      // Return null gracefully if setting not found or RLS denies access
      if (error) {
        console.warn(`Failed to fetch platform setting ${settingKey}:`, error.message);
        return null;
      }
      return data as unknown as PlatformSetting | null;
    },
    // Public settings can query immediately; private settings wait for auth
    enabled: isPublicSetting || (!authLoading && !!session?.user),
  });
  
  // For public settings, only show loading if query is loading
  // For private settings, include auth loading state
  return {
    ...query,
    isLoading: isPublicSetting 
      ? query.isLoading 
      : authLoading || (!session?.user) || query.isLoading,
  };
}
